import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@components/layout/Breadcrumb';
import SectionHeader from '@components/common/SectionHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import {
  FiPackage,
  FiSettings,
  FiTrendingUp,
  FiTruck,
  FiUsers,
  FiArchive,
  FiCheckCircle,
  FiArrowRight,
} from 'react-icons/fi';
import { SERVICES } from '@utils/constants';

/**
 * Services Page Component
 * Showcases all services offered by Bukawa ACE Ltd
 */
const Services = () => {
  const breadcrumbs = [{ label: 'Services', path: '/services' }];

  // Map icon names to components
  const iconMap = {
    package: FiPackage,
    gear: FiSettings,
    chart: FiTrendingUp,
    truck: FiTruck,
    users: FiUsers,
    warehouse: FiArchive,
  };

  // Enhanced services with detailed information
  const servicesDetails = [
    {
      id: 1,
      icon: FiPackage,
      title: 'Bulking & Collective Marketing',
      description: 'We aggregate agricultural products from our member farmers and market them collectively, ensuring better prices and market access.',
      features: [
        'Product aggregation from 1,052+ farmers',
        'Direct market linkages',
        'Better negotiating power',
        'Reduced transaction costs',
        'Quality assurance at collection',
      ],
      color: 'primary',
    },
    {
      id: 2,
      icon: FiSettings,
      title: 'Value Addition & Processing',
      description: 'Our state-of-the-art milling facility processes white maize into premium flour, adding value to raw agricultural products.',
      features: [
        'Modern milling equipment',
        'UNBS & GMP certified processing',
        'Quality control at every stage',
        '50kg packaging options',
        'Custom processing available',
      ],
      color: 'secondary',
    },
    {
      id: 3,
      icon: FiTrendingUp,
      title: 'Market Information Services',
      description: 'We gather, analyze, and disseminate real-time market information to help our members make informed production and selling decisions.',
      features: [
        'Weekly price updates',
        'Market trend analysis',
        'Demand forecasting',
        'Buyer connections',
        'SMS alerts to members',
      ],
      color: 'primary',
    },
    {
      id: 4,
      icon: FiTruck,
      title: 'Input Supply',
      description: 'Collective purchasing of quality seeds, fertilizers, and other agricultural inputs at competitive prices for our members.',
      features: [
        'Bulk purchase discounts',
        'Quality certified inputs',
        'Credit facilities available',
        'Timely delivery',
        'Technical advice included',
      ],
      color: 'secondary',
    },
    {
      id: 5,
      icon: FiUsers,
      title: 'Agricultural Extension',
      description: 'Expert advice and training on improved farming methods, crop management, and sustainable agricultural practices.',
      features: [
        'Field demonstrations',
        'Training workshops',
        'Best practice guidance',
        'Pest and disease management',
        'Soil health improvement',
      ],
      color: 'primary',
    },
    {
      id: 6,
      icon: FiArchive,
      title: 'Post-Harvest Handling',
      description: 'Professional storage, drying, and preservation services to minimize losses and maintain product quality.',
      features: [
        'Modern storage facilities',
        'Drying services',
        'Quality preservation',
        'Fumigation services',
        'Inventory management',
      ],
      color: 'secondary',
    },
  ];

  return (
    <div className="services-page">
      {/* Page Header */}
      <PageHeader
        title="Our Services"
        description="Comprehensive agricultural support services for our cooperative members and partners"
        breadcrumbs={breadcrumbs}
      />

      {/* Services Overview */}
      <section className="section bg-white">
        <div className="container-custom">
          <SectionHeader
            subtitle="What We Offer"
            title="Comprehensive Support for Farmers"
            description="From production to market, we provide end-to-end services that increase household incomes and improve agricultural value chains"
            className="mb-16"
          />

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesDetails.map((service) => {
              const Icon = service.icon;
              const colorClasses = {
                primary: {
                  bg: 'bg-bukawa-primary-50',
                  icon: 'text-bukawa-primary',
                  border: 'border-bukawa-primary-200 hover:border-bukawa-primary-300',
                },
                secondary: {
                  bg: 'bg-bukawa-secondary-50',
                  icon: 'text-bukawa-secondary-700',
                  border: 'border-bukawa-secondary-200 hover:border-bukawa-secondary-300',
                },
              };

              const colors = colorClasses[service.color];

              return (
                <Card
                  key={service.id}
                  hover
                  className={`border-2 ${colors.border} transition-all duration-300`}
                >
                  {/* Icon */}
                  <div className={`inline-flex p-4 ${colors.bg} rounded-xl mb-6`}>
                    <Icon className={`h-8 w-8 ${colors.icon}`} />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-semibold text-bukawa-gray-900 mb-3">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-bukawa-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <FiCheckCircle className={`h-5 w-5 ${colors.icon} flex-shrink-0 mt-0.5`} />
                        <span className="text-sm text-bukawa-gray-700">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section bg-bukawa-light">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Process"
            title="How We Serve Our Members"
            className="mb-16"
          />

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Member Registration',
                description: 'Join our cooperative and gain access to all our services and benefits.',
              },
              {
                step: '02',
                title: 'Production Support',
                description: 'Receive inputs, training, and technical guidance for your farming activities.',
              },
              {
                step: '03',
                title: 'Harvest & Processing',
                description: 'We collect, process, and add value to your agricultural products.',
              },
              {
                step: '04',
                title: 'Market Access',
                description: 'Benefit from collective marketing and better prices for your produce.',
              },
            ].map((item, index) => (
              <div key={index} className="relative">
                <Card className="text-center bg-white h-full">
                  {/* Step Number */}
                  <div className="text-6xl font-bold text-bukawa-secondary opacity-20 mb-4">
                    {item.step}
                  </div>

                  {/* Title */}
                  <h4 className="text-lg font-semibold text-bukawa-gray-900 mb-3">
                    {item.title}
                  </h4>

                  {/* Description */}
                  <p className="text-sm text-bukawa-gray-600">
                    {item.description}
                  </p>
                </Card>

                {/* Arrow (desktop only) */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <FiArrowRight className="h-6 w-6 text-bukawa-secondary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <SectionHeader
            subtitle="Member Benefits"
            title="Why Choose Our Services?"
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FiTrendingUp,
                title: 'Increased Income',
                description: 'Better prices through collective bargaining and market access.',
              },
              {
                icon: FiPackage,
                title: 'Quality Products',
                description: 'UNBS and GMP certified processing ensuring premium quality.',
              },
              {
                icon: FiUsers,
                title: 'Expert Support',
                description: 'Access to agricultural extension officers and technical expertise.',
              },
              {
                icon: FiArchive,
                title: 'Reduced Losses',
                description: 'Professional post-harvest handling minimizes product losses.',
              },
              {
                icon: FiTruck,
                title: 'Affordable Inputs',
                description: 'Competitive prices on seeds, fertilizers, and farming supplies.',
              },
              {
                icon: FiCheckCircle,
                title: 'Market Information',
                description: 'Real-time updates on prices, demand, and market opportunities.',
              },
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card
                  key={index}
                  hover
                  className="text-center bg-bukawa-cream border border-bukawa-gray-200"
                >
                  <div className="w-16 h-16 bg-bukawa-primary text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8" />
                  </div>
                  <h4 className="text-lg font-semibold text-bukawa-gray-900 mb-2">
                    {benefit.title}
                  </h4>
                  <p className="text-sm text-bukawa-gray-600">
                    {benefit.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-bukawa-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Ready to Access Our Services?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Join over 1,000 farmers who benefit from our comprehensive agricultural
              support services. Become a member today or contact us to learn more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                to="/membership"
                variant="secondary"
                size="lg"
              >
                Become a Member
              </Button>
              <Button
                to="/contact"
                size="lg"
                className="bg-white text-bukawa-primary hover:bg-white/90"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;